// Designs dashboard and design review status.
import { DesignResponse } from '../../../types';
import {
  BarcodeStatus,
  ForgeBarcodeReviewStatusUpdateRequest,
  ForgeBarcodeReviewStatusUpdateResponse,
  ForgeDesignsDashboardResponse,
  ForgeDesignsRefreshRequest,
  ForgeReviewSort,
  ForgeReviewStatusUpdateRequest,
  ForgeReviewStatusUpdateResponse,
} from '../../types/designs';
import { forgeRequest, forgeVoidRequest } from '../forgeRequest';
import { FORGE_DASHBOARDS_API_URL, FORGE_DESIGNS_API_URL } from './endpoints';

export type ApiReviewStatusUpdateResponse = {
  group_id?: string;
  groupId?: string;
  is_reviewed?: boolean;
  isReviewed?: boolean;
  reviewed_by?: string | null;
  reviewedBy?: string | null;
  reviewed_at?: string | null;
  reviewedAt?: string | null;
};

export function normalizeReviewStatusUpdateResponse(
  payload: ApiReviewStatusUpdateResponse,
  groupId: string,
  fallbackIsReviewed: boolean
): ForgeReviewStatusUpdateResponse {
  return {
    groupId: payload.groupId ?? payload.group_id ?? groupId,
    isReviewed: payload.isReviewed ?? payload.is_reviewed ?? fallbackIsReviewed,
    reviewedBy: payload.reviewedBy ?? payload.reviewed_by ?? null,
    reviewedAt: payload.reviewedAt ?? payload.reviewed_at ?? null,
  };
}

export async function fetchDesignsDashboard(
  token: string,
  filters?: {
    chainId?: number | null;
    barcodeStatus?: BarcodeStatus | null;
    query?: string | null;
    updatedBy?: string | null;
    updatedByValues?: string[] | null;
    createdByValues?: string[] | null;
    groupByNormalizedBarcode?: boolean;
    reviewSort?: ForgeReviewSort | null;
    page?: number;
    limit?: number;
  }
): Promise<ForgeDesignsDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.barcodeStatus) params.set('barcode_status', filters.barcodeStatus);
  if (filters?.query) params.set('query', filters.query);
  if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
  filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
  filters?.createdByValues?.forEach((value) => params.append('created_by_values', value));
  if (filters?.groupByNormalizedBarcode) params.set('group_by_normalized_barcode', 'true');
  if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  const requestKey = `${token}:${query}`;
  return forgeRequest<ForgeDesignsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/designs${query ? `?${query}` : ''}`,
    'Error fetching Forge designs dashboard',
    { dedupeKey: ['fetchDesignsDashboard', requestKey] }
  );
}

export async function refreshDesignsDashboard(
  token: string,
  request: ForgeDesignsRefreshRequest
): Promise<ForgeDesignsDashboardResponse> {
  return forgeRequest<ForgeDesignsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/designs/refresh`,
    'Error refreshing Forge designs dashboard',
    { method: 'POST', body: request }
  );
}

export async function updateDesignReviewStatus(
  token: string,
  designId: string,
  request: ForgeReviewStatusUpdateRequest
): Promise<DesignResponse> {
  return forgeRequest<DesignResponse>(
    token,
    `${FORGE_DESIGNS_API_URL}/${designId}/review`,
    'Error updating Forge design review status',
    { method: 'PATCH', body: request }
  );
}

export async function updateDesignReviewStatusByNormalizedBarcode(
  token: string,
  request: ForgeBarcodeReviewStatusUpdateRequest
): Promise<ForgeBarcodeReviewStatusUpdateResponse> {
  return forgeRequest<ForgeBarcodeReviewStatusUpdateResponse>(
    token,
    `${FORGE_DESIGNS_API_URL}/review/by-normalized-barcode`,
    'Error updating Forge Design review status',
    { method: 'PATCH', body: request }
  );
}

export async function deleteEmptyDesign(token: string, designId: string): Promise<void> {
  await forgeVoidRequest(
    token,
    `${FORGE_DESIGNS_API_URL}/${encodeURIComponent(designId)}?require_empty=true`,
    'Error deleting empty design',
    { method: 'DELETE' }
  );
}
